// utils/mediaSharing.js
// "Share this movie/show" from the Media Detail header. Separate from coin
// sharing: neutral copy, no sharer identity or tier.
//
// Web Share API when the browser has it, otherwise copy the link. Links always
// point at https://aluvo.app so they open the installed app via Universal /
// App Links, whichever host the page was shared from.
import {
    MEDIA_SHARE_METHODS,
    MEDIA_SHARE_SOURCE,
    buildMediaShareText,
    buildMediaShareTitle,
    buildMediaShareUrl,
    isMediaShareLink,
} from '@aluvo/constants/src/mediaShare';
import { ANALYTICS_EVENTS, trackEvent } from '../analytics';

function analyticsProps({ kind, tmdbId, seasonNumber }, sourceScreen) {
    return {
        media_type: kind,
        media_id: Number(tmdbId),
        season_number: seasonNumber != null ? Number(seasonNumber) : null,
        source_screen: sourceScreen,
    };
}

async function copyLink(url, nav) {
    if (!nav?.clipboard?.writeText) return false;
    try {
        await nav.clipboard.writeText(url);
        return true;
    } catch {
        return false;
    }
}

/**
 * @param {object} media  { kind, tmdbId, title, year?, seasonNumber?, seasonName? }
 * @returns {Promise<'shared'|'copied'|'dismissed'|'error'>}
 */
export async function shareMedia(media, { sourceScreen, nav = typeof navigator !== 'undefined' ? navigator : null } = {}) {
    const url = buildMediaShareUrl(media);
    if (!url) return 'error';

    const props = analyticsProps(media, sourceScreen);
    trackEvent(ANALYTICS_EVENTS.MEDIA_SHARE_OPENED, props);

    if (typeof nav?.share === 'function') {
        try {
            await nav.share({ title: buildMediaShareTitle(media), text: buildMediaShareText(media), url });
            trackEvent(ANALYTICS_EVENTS.MEDIA_SHARE_COMPLETED, { ...props, share_method: MEDIA_SHARE_METHODS.WEB_SHARE });
            return 'shared';
        } catch (err) {
            // The user closed the sheet — not an error, and no fallback copy.
            if (err?.name === 'AbortError') return 'dismissed';
            // Otherwise (e.g. NotAllowedError) fall through to copying the link.
        }
    }

    if (await copyLink(url, nav)) {
        trackEvent(ANALYTICS_EVENTS.MEDIA_SHARE_LINK_COPIED, { ...props, share_method: MEDIA_SHARE_METHODS.COPY_LINK });
        return 'copied';
    }
    return 'error';
}

/** Records an arrival from a shared media link (once per title per page load). */
export function trackSharedMediaLinkOpened(media, href = typeof window !== 'undefined' ? window.location.href : '') {
    if (!isMediaShareLink(href)) return false;
    const { media_type, media_id, season_number } = analyticsProps(media);
    trackEvent(
        ANALYTICS_EVENTS.MEDIA_SHARED_LINK_OPENED,
        { media_type, media_id, season_number, source: MEDIA_SHARE_SOURCE },
        { onceKey: `${media_type}:${media_id}:${season_number ?? ''}` },
    );
    return true;
}
